import React from 'react';

/** Why an invoice is rejected; the codes match invoice_corrections.REJECT_CODES on the server. */
export const REJECT_CODES = [
  ['misread', 'Read wrongly'],
  ['wrong_supplier', 'Not this supplier / not for us'],
  ['duplicate', 'Already received'],
  ['not_an_invoice', 'Not an invoice'],
  ['other', 'Something else'],
];

export const PROBLEM_FIELDS = [
  ['invoice_total', 'Total', false],
  ['vat_total', 'VAT', false],
  ['date', 'Date', false],
  ['invoice_no', 'Invoice number', false],
  ['supplier_name', 'Supplier', false],
  ['qty', 'Line quantity', true],
  ['unit_price', 'Line unit price', true],
  ['line_total', 'Line total', true],
  ['raw_desc', 'Line description', true],
];
const isLineField = (field) => !!PROBLEM_FIELDS.find(([f]) => f === field)?.[2];

/** What the reader read for a field, to show beside "what the document says". */
export function readOf(invoice, field, lineIndex) {
  const ln = (invoice.lines || [])[lineIndex] || {};
  const v = {
    invoice_total: invoice.totals?.invoice_total, vat_total: invoice.totals?.vat_total, date: invoice.date,
    invoice_no: invoice.invoice_no, supplier_name: invoice.supplier_name,
    qty: ln.qty, unit_price: ln.unit_price ?? ln.unit_cost, line_total: ln.line_total, raw_desc: ln.raw_desc ?? ln.description,
  }[field];
  return v === null || v === undefined || v === 'None' ? '' : String(v);
}

/** The problems in the shape the API takes: only rows with a field, each with what was read. */
export function problemsPayload(invoice, rows) {
  return rows.filter((r) => r.field).map((r) => {
    const line = isLineField(r.field);
    const idx = line ? Number(r.line_index || 0) : null;
    return { field: r.field, ...(line ? { line_index: idx } : {}), read: readOf(invoice, r.field, idx) || null,
      actual: (r.actual || '').trim() || null };
  });
}

/**
 * The structured part of a rejection: a reason code and, for "read wrongly", which figures were wrong and what the
 * document actually says. This is what the reader's accuracy is measured from.
 */
export default function RejectDetails({ invoice, code, onCode, rows, onRows }) {
  const lines = invoice.lines || [];
  const setRow = (i, patch) => onRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const sel = 'min-h-[40px] rounded-lg border border-surface-border bg-surface px-2 text-ink';
  return (
    <div className="space-y-3" data-testid="reject-details">
      <fieldset>
        <legend className="text-ink mb-1">What was wrong?</legend>
        <div className="flex flex-wrap gap-2">
          {REJECT_CODES.map(([c, label]) => (
            <label key={c} className={`inline-flex items-center gap-2 min-h-[40px] px-3 rounded-full border ${code === c ? 'border-primary-500 bg-primary-50 text-ink' : 'border-surface-border'}`}>
              <input type="radio" name="reject-code" value={c} checked={code === c} onChange={() => onCode(c)} data-testid={`reject-code-${c}`} />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
      {code === 'misread' && (
        <div className="space-y-2" data-testid="reject-problems">
          <p className="text-ink">Which figures were read wrongly?</p>
          {rows.map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1fr_1fr] gap-2 items-end">
              <label className="text-xs">Field
                <select className={`${sel} w-full`} value={r.field || ''} onChange={(e) => setRow(i, { field: e.target.value })}
                  data-testid={`reject-problem-field-${i}`}>
                  <option value="">Choose…</option>
                  {PROBLEM_FIELDS.map(([f, label]) => <option key={f} value={f}>{label}</option>)}
                </select>
              </label>
              {isLineField(r.field) ? (
                <label className="text-xs">Line
                  <select className={`${sel} w-full`} value={r.line_index ?? 0} onChange={(e) => setRow(i, { line_index: Number(e.target.value) })}
                    data-testid={`reject-problem-line-${i}`}>
                    {lines.map((ln, k) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <option key={k} value={k}>{`${k + 1}. ${(ln.raw_desc ?? ln.description ?? '').slice(0, 40)}`}</option>
                    ))}
                  </select>
                </label>
              ) : <span className="text-xs">{r.field ? `Read as: ${readOf(invoice, r.field, null) || '(nothing)'}` : ''}</span>}
              <label className="text-xs">The document says
                <input className={`${sel} w-full`} value={r.actual || ''} onChange={(e) => setRow(i, { actual: e.target.value })}
                  data-testid={`reject-problem-actual-${i}`} />
              </label>
              {isLineField(r.field) && (
                <span className="text-xs sm:col-span-3">Read as: {readOf(invoice, r.field, r.line_index ?? 0) || '(nothing)'}</span>
              )}
            </div>
          ))}
          <button type="button" onClick={() => onRows([...rows, { field: '', line_index: 0, actual: '' }])}
            data-testid="reject-problem-add" className="min-h-[40px] px-3 rounded-lg border border-surface-border text-ink">
            Add another
          </button>
        </div>
      )}
    </div>
  );
}
